/**
 * AdWup analytics client.
 *
 * Sends events to BOTH PostHog (visualization) AND our backend /api/track endpoint
 * (used for abandoned-session email triggers and admin reporting).
 *
 * Usage:
 *   import { track, identify, EVENTS } from "../lib/analytics";
 *   track(EVENTS.LISTING_URL_ENTERED, { url: "..." });
 *   track(EVENTS.LISTING_URL_ENTERED, { url, name }, { email });
 */
import posthog from "posthog-js";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";
const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";

const DISTINCT_ID_KEY = "adwup_distinct_id";
const STORED_EMAIL_KEY = "adwup_funnel_email";

// ---------- distinct id ----------

function _generateDistinctId() {
  // RFC4122-ish, no crypto dep needed
  return "anon-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
}

export function getDistinctId() {
  if (typeof window === "undefined") return "ssr";
  let id = localStorage.getItem(DISTINCT_ID_KEY);
  if (!id) {
    id = _generateDistinctId();
    localStorage.setItem(DISTINCT_ID_KEY, id);
  }
  return id;
}

// ---------- bootstrap ----------

let _initialized = false;

export function initAnalytics() {
  if (_initialized) return;
  _initialized = true;
  if (POSTHOG_KEY) {
    try {
      posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        capture_pageview: false,        // we send manually for finer control
        capture_pageleave: true,
        persistence: "localStorage+cookie",
        autocapture: false,             // we use explicit events
        bootstrap: { distinctID: getDistinctId() },
      });
    } catch (e) {
      // posthog can crash in some sandboxed iframes — silent fallback
      // backend tracking still works
      if (typeof console !== "undefined") console.warn("[analytics] PostHog init failed:", e?.message);
    }
  }
}

// ---------- event names ----------

export const EVENTS = {
  // Landing
  LANDING_VIEWED: "landing_viewed",
  SCRAPE_BAR_VIEWED: "scrape_bar_viewed",
  LISTING_URL_ENTERED: "listing_url_entered",
  SCRAPE_CLICKED: "scrape_clicked",
  SCRAPE_STARTED: "scrape_started",
  SCRAPE_COMPLETED: "scrape_completed",
  SCRAPE_FAILED: "scrape_failed",
  // Auth
  SIGNUP_PAGE_VIEWED: "signup_page_viewed",
  REGISTRATION_STARTED: "registration_started",
  REGISTRATION_COMPLETED: "registration_completed",
  REGISTRATION_ABANDONED: "registration_abandoned",
  LOGIN_COMPLETED: "login_completed",
  FORGOT_PASSWORD_CLICKED: "forgot_password_clicked",
  PASSWORD_RESET_COMPLETED: "password_reset_completed",
  // Trial + Payment
  PRICING_PAGE_VIEWED: "pricing_page_viewed",
  PLAN_SELECTED: "plan_selected",
  TRIAL_STARTED: "trial_started",
  CHECKOUT_STARTED: "checkout_started",
  CHECKOUT_ABANDONED: "checkout_abandoned",
  PAYMENT_COMPLETED: "payment_completed",
  SUBSCRIPTION_ACTIVATED: "subscription_activated",
  PAYMENT_FAILED: "payment_failed",
  SUBSCRIPTION_CANCELED: "subscription_canceled",
  PLAN_UPGRADED: "plan_upgraded",
  PLAN_DOWNGRADED: "plan_downgraded",
  // Product
  DASHBOARD_VIEWED: "dashboard_viewed",
  LISTING_ANALYZED: "listing_analyzed",
  AI_COPY_GENERATED: "ai_copy_generated",
  IMAGE_GENERATED: "image_generated",
  CAMPAIGN_CREATED: "campaign_created",
  CAMPAIGN_LAUNCHED: "campaign_launched",
  FACEBOOK_CONNECTED: "facebook_connected",
  INTEGRATION_CONNECTED: "integration_connected",
  VOICE_CONFIG_SAVED: "voice_config_saved",
};

// ---------- email association ----------

export function setStoredFunnelEmail(email) {
  if (typeof window === "undefined" || !email) return;
  try { localStorage.setItem(STORED_EMAIL_KEY, email); } catch (e) {
    // localStorage can be disabled (private mode, quota); funnel still works without email persistence
    if (typeof console !== "undefined") console.debug("[analytics] localStorage unavailable:", e?.message);
  }
}

export function getStoredFunnelEmail() {
  if (typeof window === "undefined") return null;
  try { return localStorage.getItem(STORED_EMAIL_KEY); } catch (e) {
    if (typeof console !== "undefined") console.debug("[analytics] localStorage read failed:", e?.message);
    return null;
  }
}

// ---------- main API ----------

/**
 * Capture a funnel event in PostHog AND backend.
 * @param {string} event       — one of EVENTS.*
 * @param {object} properties  — arbitrary, no PII / no payment data
 * @param {object} opts        — { email } to attach a contact for follow-up
 */
export function track(event, properties = {}, opts = {}) {
  const distinctId = getDistinctId();
  const email = opts.email || getStoredFunnelEmail() || null;
  if (opts.email) setStoredFunnelEmail(opts.email);

  // PostHog (best-effort)
  if (POSTHOG_KEY) {
    try { posthog.capture(event, { ...properties, distinct_id: distinctId }); } catch (e) {
      if (typeof console !== "undefined") console.debug("[analytics] posthog capture failed:", e?.message);
    }
  }

  // Backend (always — fire-and-forget, no awaiting)
  try {
    fetch(`${API_URL}/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        event,
        distinct_id: distinctId,
        properties,
        user_email: email,
      }),
      // Use keepalive so events fire even on navigation away
      keepalive: true,
    }).catch((e) => {
      if (typeof console !== "undefined") console.debug("[analytics] backend track failed:", e?.message);
    });
  } catch (e) {
    // network errors should never break the calling flow
    if (typeof console !== "undefined") console.debug("[analytics] track threw:", e?.message);
  }
}

/** Bind anonymous distinct_id to a real user post-login. */
export function identify(user) {
  const distinctId = getDistinctId();
  if (POSTHOG_KEY && user) {
    try {
      posthog.identify(distinctId, {
        email: user.email,
        name: user.name,
        plan: user.plan,
      });
    } catch (e) {
      if (typeof console !== "undefined") console.debug("[analytics] posthog identify failed:", e?.message);
    }
  }
  if (user?.email) setStoredFunnelEmail(user.email);
  // Bind on backend (cookies sent automatically because credentials:'include' is default in axios)
  try {
    fetch(`${API_URL}/identify`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ distinct_id: distinctId }),
      keepalive: true,
    }).catch((e) => {
      if (typeof console !== "undefined") console.debug("[analytics] backend identify failed:", e?.message);
    });
  } catch (e) {
    if (typeof console !== "undefined") console.debug("[analytics] identify threw:", e?.message);
  }
}

export function reset() {
  if (POSTHOG_KEY) {
    try { posthog.reset(); } catch (e) {
      if (typeof console !== "undefined") console.debug("[analytics] posthog reset failed:", e?.message);
    }
  }
  // Generate fresh distinct_id so logged-out events don't merge into the previous user
  if (typeof window !== "undefined") {
    localStorage.removeItem(DISTINCT_ID_KEY);
    localStorage.removeItem(STORED_EMAIL_KEY);
  }
}
